/**
 * HTML to Thumbnail Generator Utility
 * A pure JavaScript utility for generating thumbnails from HTML content
 * 
 * Usage:
 * import { generateThumbnail, downloadThumbnail } from './thumbnailGenerator';
 * const handleGenerateThumbnail = async () => {
  try {
    const dataUrl = await generateThumbnail(htmlCode, 300, 200, {
      quality: 0.9,
      format: 'png',
      backgroundColor: '#ffffff'
    });
    
    // Use the dataUrl (display in img tag, download, etc.)
    setThumbnailSrc(dataUrl);
  } catch (error) {
    console.error('Error:', error);
  }
};
 */

/**
 * Generate a thumbnail from HTML code
 * @param {string} htmlCode - The HTML content to render
 * @param {number} width - Thumbnail width in pixels
 * @param {number} height - Thumbnail height in pixels
 * @param {Object} options - Additional options
 * @returns {Promise<string>} - Promise that resolves to base64 data URL of the thumbnail
 */
export async function generateThumbnail(htmlCode, width = 300, height = 200, options = {}) {
    const {
        quality = 0.9,
        format = 'png', // 'png' or 'jpeg'
        backgroundColor = '#ffffff',
        scale = 2 // Render scale for better quality
    } = options;

    return new Promise((resolve, reject) => {
        try {
            // Create a temporary iframe for rendering
            const iframe = document.createElement('iframe');
            iframe.style.position = 'absolute';
            iframe.style.left = '-9999px';
            iframe.style.top = '-9999px';
            iframe.style.width = (width * scale) + 'px';
            iframe.style.height = (height * scale) + 'px';
            iframe.style.border = 'none';
            iframe.style.visibility = 'hidden';
            
            document.body.appendChild(iframe);
            
            // Prepare HTML content with proper scaling
            const processedHtml = `
                <!DOCTYPE html>
                <html>
                <head>
                    <meta charset="utf-8">
                    <meta name="viewport" content="width=device-width, initial-scale=1">
                    <style>
                        body { 
                            margin: 0; 
                            padding: 0; 
                            font-family: Arial, sans-serif;
                            background-color: ${backgroundColor};
                            overflow: hidden;
                            transform-origin: top left;
                        }
                        * { 
                            box-sizing: border-box; 
                        }
                        img {
                            max-width: 100%;
                            height: auto;
                        }
                        table {
                            border-collapse: collapse;
                        }
                    </style>
                </head>
                <body>
                    ${cleanHtmlContent(htmlCode)}
                </body>
                </html>
            `;
            
            // Write content to iframe
            iframe.contentDocument.open();
            iframe.contentDocument.write(processedHtml);
            iframe.contentDocument.close();
            
            // Wait for content to load
            iframe.onload = async () => {
                try {
                    // Wait for images and other resources to load
                    await waitForResources(iframe.contentDocument);
                    
                    // Generate thumbnail using different methods
                    const thumbnailDataUrl = await captureIframeContent(iframe, width, height, scale, format, quality);
                    
                    // Clean up
                    document.body.removeChild(iframe);
                    
                    resolve(thumbnailDataUrl);
                } catch (error) {
                    document.body.removeChild(iframe);
                    reject(error);
                }
            };
            
            iframe.onerror = (error) => {
                document.body.removeChild(iframe);
                reject(new Error('Failed to load iframe content'));
            };
            
            // Fallback timeout
            setTimeout(() => {
                if (document.body.contains(iframe)) {
                    captureIframeContent(iframe, width, height, scale, format, quality)
                        .then(result => {
                            document.body.removeChild(iframe);
                            resolve(result);
                        })
                        .catch(error => {
                            document.body.removeChild(iframe);
                            reject(error);
                        });
                }
            }, 5000);
            
        } catch (error) {
            reject(error);
        }
    });
}

/**
 * Clean HTML content by removing problematic elements
 * @private
 */
function cleanHtmlContent(htmlCode) {
    // Remove DOCTYPE, html, head, and body tags to avoid conflicts
    let cleaned = htmlCode
        .replace(/<!DOCTYPE[^>]*>/gi, '')
        .replace(/<html[^>]*>/gi, '')
        .replace(/<\/html>/gi, '')
        .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '')
        .replace(/<body[^>]*>/gi, '')
        .replace(/<\/body>/gi, '');
    
    // Add crossorigin to images for better compatibility
    cleaned = cleaned.replace(/<img([^>]*?)>/gi, (match, attrs) => {
        if (!attrs.includes('crossorigin')) {
            return `<img${attrs} crossorigin="anonymous">`;
        }
        return match;
    });
    
    return cleaned.trim();
}

/**
 * Wait for all resources (images, etc.) to load
 * @private
 */
function waitForResources(doc) {
    return new Promise((resolve) => {
        const images = doc.querySelectorAll('img');
        const promises = [];
        
        images.forEach(img => {
            if (!img.complete) {
                promises.push(new Promise(imgResolve => {
                    img.onload = imgResolve;
                    img.onerror = imgResolve; // Resolve even on error
                    // Timeout for slow images
                    setTimeout(imgResolve, 3000);
                }));
            }
        });
        
        // Wait for all images or timeout
        Promise.all(promises).then(resolve);
        setTimeout(resolve, 4000); // Maximum wait time
    });
}

/**
 * Capture iframe content as image
 * @private
 */
async function captureIframeContent(iframe, width, height, scale, format, quality) {
    try {
        // Method 1: SVG foreignObject approach
        return await captureUsingSVG(iframe, width, height, scale, format, quality);
    } catch (error) {
        console.warn('SVG method failed, trying canvas method');
        try {
            // Method 2: Direct canvas approach
            return await captureUsingCanvas(iframe, width, height, scale, format, quality);
        } catch (canvasError) {
            console.warn('Canvas method failed, using fallback');
            // Method 3: Fallback
            return createFallbackThumbnail(width, height, format, quality);
        }
    }
}

/**
 * Capture using SVG foreignObject method
 * @private
 */
function captureUsingSVG(iframe, width, height, scale, format, quality) {
    return new Promise((resolve, reject) => {
        try {
            const doc = iframe.contentDocument;
            const serializer = new XMLSerializer();
            const htmlString = serializer.serializeToString(doc.documentElement);
            
            const svgData = `
                <svg xmlns="http://www.w3.org/2000/svg" width="${width * scale}" height="${height * scale}">
                    <foreignObject width="100%" height="100%">
                        ${htmlString}
                    </foreignObject>
                </svg>
            `;
            
            const canvas = document.createElement('canvas');
            canvas.width = width * scale;
            canvas.height = height * scale;
            const ctx = canvas.getContext('2d');
            
            const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(svgBlob);
            
            const img = new Image();
            img.onload = () => {
                ctx.drawImage(img, 0, 0);
                
                // Scale down to final size
                const finalCanvas = document.createElement('canvas');
                finalCanvas.width = width;
                finalCanvas.height = height;
                const finalCtx = finalCanvas.getContext('2d');
                
                finalCtx.drawImage(canvas, 0, 0, width * scale, height * scale, 0, 0, width, height);
                
                const mimeType = format === 'jpeg' ? 'image/jpeg' : 'image/png';
                const dataUrl = finalCanvas.toDataURL(mimeType, quality);
                
                URL.revokeObjectURL(url);
                resolve(dataUrl);
            };
            
            img.onerror = () => {
                URL.revokeObjectURL(url);
                reject(new Error('Failed to load SVG image'));
            };
            
            img.src = url;
        } catch (error) {
            reject(error);
        }
    });
}

/**
 * Capture using direct canvas method
 * @private
 */
function captureUsingCanvas(iframe, width, height, scale, format, quality) {
    return new Promise((resolve, reject) => {
        try {
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            
            // Fill background
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, width, height);
            
            // Try to draw iframe content (limited browser support)
            const doc = iframe.contentDocument;
            const body = doc.body;
            
            if (body) {
                // This is a simplified approach - may not work in all browsers
                const rect = body.getBoundingClientRect();
                const scaleX = width / rect.width;
                const scaleY = height / rect.height;
                const finalScale = Math.min(scaleX, scaleY);
                
                ctx.scale(finalScale, finalScale);
                
                // Note: This method has limited browser support
                // Most browsers don't allow direct canvas drawing of iframe content
                throw new Error('Canvas method not supported');
            }
            
            reject(new Error('No content found'));
        } catch (error) {
            reject(error);
        }
    });
}

/**
 * Create a fallback thumbnail
 * @private
 */
function createFallbackThumbnail(width, height, format, quality) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    
    // Create a simple placeholder
    ctx.fillStyle = '#f0f0f0';
    ctx.fillRect(0, 0, width, height);
    
    ctx.strokeStyle = '#ddd';
    ctx.strokeRect(0, 0, width, height);
    
    ctx.fillStyle = '#666';
    ctx.font = '16px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Email Preview', width / 2, height / 2 - 10);
    
    ctx.font = '12px Arial';
    ctx.fillStyle = '#999';
    ctx.fillText('Thumbnail Generated', width / 2, height / 2 + 10);
    
    const mimeType = format === 'jpeg' ? 'image/jpeg' : 'image/png';
    return canvas.toDataURL(mimeType, quality);
}

/**
 * Download the thumbnail
 * @param {string} dataUrl - Base64 data URL of the image
 * @param {string} filename - Optional filename (auto-generated if not provided)
 */
export function downloadThumbnail(dataUrl, filename = null) {
    const link = document.createElement('a');
    link.download = filename || `thumbnail-${Date.now()}.png`;
    link.href = dataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

/**
 * Convert data URL to blob
 * @param {string} dataURL - Base64 data URL
 * @returns {Blob} - Blob object
 */
export function dataURLtoBlob(dataURL) {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
}

/**
 * Generate thumbnail and return as blob
 * @param {string} htmlCode - The HTML content to render
 * @param {number} width - Thumbnail width in pixels
 * @param {number} height - Thumbnail height in pixels
 * @param {Object} options - Additional options
 * @returns {Promise<Blob>} - Promise that resolves to blob
 */
export async function generateThumbnailBlob(htmlCode, width = 300, height = 200, options = {}) {
    const dataUrl = await generateThumbnail(htmlCode, width, height, options);
    return dataURLtoBlob(dataUrl);
}

// Default export
export default {
    generateThumbnail,
    generateThumbnailBlob,
    downloadThumbnail,
    dataURLtoBlob
};